import {
  IconCirclePlus,
  IconClipboardCheck,
  IconId,
  IconNotebook,
} from '@tabler/icons-react';
import { Icon, Stack } from '@zorro/web/design-system';
import {
  callEndpoint,
  sortEnrollments,
  useAppRouter,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { ReactNode, useMemo, useState } from 'react';

import { useAnalytics } from '../Analytics';
import { AppLayout } from '../AppLayout';
import {
  EmployeeLayoutAction,
  EmployeeLayoutActions,
} from './EmployeeLayoutActions';
import {
  EmployeeLayoutSidebar,
  EmployeeLayoutSidebarType,
  SidebarItemProps,
} from './EmployeeLayoutSidebar/EmployeeLayoutSidebar';
import { EmployeeLayoutSidebarSubitemProps } from './EmployeeLayoutSidebar/EmployeeLayoutSidebarSubitem';
import { mapOnboardingPeriodToEmployeeSidebarSubitem } from './EmployeeLayoutUtils';

type Props = {
  page: EmployeeLayoutSidebarType;
  employerId: string;
  employeeId: string;
  onboardingPeriodId?: string;
  children: ReactNode;
  isDon?: boolean;
};

export const EmployeeLayout = ({
  page,
  employerId,
  employeeId,
  onboardingPeriodId,
  children,
  isDon = false,
}: Props) => {
  const { isEmployerAdmin, isLoading: isRolesLoading } = useRoles();
  const { sendEvent } = useAnalytics();
  const [action, setAction] = useState<EmployeeLayoutAction>();

  const { navigateToRoute } = useAppRouter();

  const {
    data: onboardingPeriods = [],
    isLoading: isOnboardingPeriodsLoading,
  } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateEmployeeOnboardingReport',
    params: [employeeId],
  });
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const enrollmentSubitems: EmployeeLayoutSidebarSubitemProps[] =
    useMemo(() => {
      const subitems =
        onboardingPeriods.length > 0
          ? onboardingPeriods.sort(sortEnrollments).map((onboardingPeriod) => {
              const handleClick = () => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                isDon
                  ? navigateToRoute('DON_EMPLOYEE_PLANS_TAB', [
                      {
                        employerId,
                        employeeId,
                        onboardingPeriodId: onboardingPeriod.id,
                      },
                    ])
                  : navigateToRoute('OPS_EMPLOYEE_PLANS_TAB', [
                      {
                        employerId,
                        employeeId,
                        onboardingPeriodId: onboardingPeriod.id,
                      },
                    ]);
              };

              return mapOnboardingPeriodToEmployeeSidebarSubitem({
                currentOnboardingPeriodId: onboardingPeriodId,
                onClick: handleClick,
                onboardingPeriod,
              });
            })
          : [
              {
                id: 'no-enrollments',
                label: 'No enrollments yet',
                isChecked: page === EmployeeLayoutSidebarType.ENROLLMENTS,
                onClick: () =>
                  isDon
                    ? navigateToRoute('DON_EMPLOYEE_NO_ENROLLMENTS', [
                        { employerId, employeeId },
                      ])
                    : navigateToRoute('OPS_EMPLOYEE_NO_ENROLLMENTS', [
                        { employerId, employeeId },
                      ]),
              },
            ];

      if (!isRolesLoading && !isEmployerAdmin) {
        subitems.push({
          id: 'new-enrollment',
          label: 'New enrollment',
          onClick: () => {
            void sendEvent('click_new_enrollment');
            setAction(EmployeeLayoutAction.ADD_ENROLLMENT);
          },
          leftContent: <Icon icon={IconCirclePlus} size={20} />,
          align: 'center',
          justify: 'center',
        });
      }

      return subitems;
    }, [
      onboardingPeriods,
      page,
      isRolesLoading,
      isEmployerAdmin,
      onboardingPeriodId,
      employee,
      navigateToRoute,
      employerId,
      employeeId,
    ]);

  const sideBarItems: SidebarItemProps[] = [
    {
      page: EmployeeLayoutSidebarType.PERSONAL_DETAILS,
      label: 'Personal details',
      icon: IconId,
      subitems: [],
      onClick: () => {
        void sendEvent('click_tab', { tab: 'EMPLOYEE_PERSONAL_DETAILS' });
        if (isDon) {
          navigateToRoute('DON_EMPLOYEE_PERSONAL_DETAILS', [
            { employeeId, employerId },
          ]);
        } else {
          navigateToRoute('OPS_EMPLOYEE_PERSONAL_DETAILS', [
            { employeeId, employerId },
          ]);
        }
      },
    },
    {
      page: EmployeeLayoutSidebarType.ENROLLMENTS,
      label: 'Enrollments',
      icon: IconClipboardCheck,
      subitems: enrollmentSubitems,
      onClick: () => {
        void sendEvent('click_tab', { tab: 'EMPLOYEE_ENROLLMENTS' });
      },
    },
  ];

  if (!isRolesLoading && !isEmployerAdmin) {
    sideBarItems.push({
      page: EmployeeLayoutSidebarType.GENERAL_NOTES,
      label: 'General notes',
      icon: IconNotebook,
      onClick: () => {
        void sendEvent('click_tab', { tab: 'EMPLOYEE_GENERAL_NOTES' });
        if (isDon) {
          navigateToRoute('DON_EMPLOYEE_GENERAL_NOTES', [
            { employeeId, employerId },
          ]);
        } else {
          navigateToRoute('OPS_EMPLOYEE_GENERAL_NOTES', [
            { employeeId, employerId },
          ]);
        }
      },
      subitems: [],
    });
  }

  const handleOnboardingPeriodsChange = async () => {
    if (onboardingPeriodId) {
      try {
        const currentOnboardingPeriod = await callEndpoint({
          method: 'onboardingPeriodsControllerFindOne',
          params: [employeeId, onboardingPeriodId],
        });

        if (!currentOnboardingPeriod) {
          navigateToRoute(isDon ? 'DON_EMPLOYEE_PAGE' : 'OPS_EMPLOYEE_PAGE', [
            { employerId, employeeId },
          ]);
        }
      } catch {
        navigateToRoute(isDon ? 'DON_EMPLOYEE_PAGE' : 'OPS_EMPLOYEE_PAGE', [
          { employerId, employeeId },
        ]);
      }
    }
  };

  return (
    <AppLayout withSearch={!isDon} sidebar={{ isAlwaysOpen: true }}>
      <AppLayout.Sidebar>
        <EmployeeLayoutSidebar
          isLoading={isOnboardingPeriodsLoading || isRolesLoading}
          employerId={employerId}
          employeeId={employeeId}
          isDon={isDon}
          setAction={setAction}
          items={sideBarItems}
          page={page}
        />
      </AppLayout.Sidebar>
      <AppLayout.Main>
        <Stack>{children}</Stack>
      </AppLayout.Main>
      <EmployeeLayoutActions
        onboardingPeriodId={onboardingPeriodId}
        employeeId={employeeId}
        employerId={employerId}
        setAction={setAction}
        action={action}
        onChangeOnboardingPeriod={handleOnboardingPeriodsChange}
      />
    </AppLayout>
  );
};
